import { TranscriptionStatus } from "@shared/types";

import api from "./auth.api";

/** Состояние авто-сводки (генерируется автоматически после расшифровки) */
export type AiSummaryStatus = "pending" | "ready" | "failed" | "skipped";

export interface CreateTranscriptionRequest {
  fileId?: number;
  videoUrl?: string;
  fileName: string;
  language?: string;
  model?: string;
  estimatedMinutes?: number;
  detectLanguage?: boolean;
  preview?: boolean;
}

export interface TranscriptionJob {
  jobId: string;
  status: TranscriptionStatus;
  isPreview: boolean;
}

export interface TranscriptionSegment {
  start: number;
  end: number;
  // AssemblyAI отдаёт слово в `text`, Deepgram — в `word`/`punctuated_word`
  text?: string;
  word?: string;
  punctuated_word?: string;
  speaker?: string | number;
  confidence?: number;
}

export interface TranscriptionUtterance {
  speaker: number;
  transcript: string;
  start: number;
  end: number;
  confidence: number;
  /** Пословные тайминги реплики (для подсветки на вкладке «По спикерам») */
  words?: TranscriptionSegment[];
}

export interface GroupedUtterance {
  speaker: number;
  transcript: string;
  start: number;
  end: number;
  confidence: number;
  /** Пословные тайминги реплики (для подсветки на вкладке «По спикерам») */
  words?: TranscriptionSegment[];
}

export interface TranscriptionResult {
  id: string;
  fileId: number | null;
  fileName: string;
  text: string;
  language: string;
  duration: number;
  provider?: string;
  status: TranscriptionStatus;
  segments?: TranscriptionSegment[];
  utterances?: TranscriptionUtterance[];
  groupedUtterances?: GroupedUtterance[];
  speakerNames?: Record<number, string>;
  aiSummary?: {
    brief: string;
    keyPoints: string[];
    generatedAt: string;
  };
  /** Состояние авто-сводки: приходит и в лёгком списке, и в детальном ответе */
  aiSummaryStatus?: AiSummaryStatus | null;
  createdAt: string;
  updatedAt?: string;
  isPreview: boolean;
  previewLimitSeconds: number | null;
  totalDurationSeconds: number | null;
  recommendedPlan: "trial" | "basic" | "pro" | null;
  sourceAvailable: boolean;
  sourceExpiresAt?: string | null;
  requestedModel?: "nova-3" | "assemblyai" | null;
  sourcePreviewId?: string | null;
  upgradeInProgressId?: string | null;
  /** Свой вариант SRT — если задан, показывается вместо автогенерации */
  customSrt?: string | null;
  /** Клиентский флаг: подгружены ли тяжёлые детали (segments/utterances/aiSummary) */
  isDetailLoaded?: boolean;
}

export interface PaginatedHistoryQuery {
  page?: number;
  limit?: number;
  search?: string;
}

/**
 * Элемент списка истории: бекенд отдаёт облегчённый DTO без тяжёлых полей
 * и с обрезанным text — полную версию даёт только getResult
 */
export type TranscriptionListItem = Omit<
  TranscriptionResult,
  "segments" | "utterances" | "groupedUtterances" | "speakerNames" | "aiSummary" | "customSrt" | "provider"
>;

export interface PaginatedTranscriptionResult {
  items: TranscriptionResult[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedTranscriptionListResult extends Omit<PaginatedTranscriptionResult, "items"> {
  items: TranscriptionListItem[];
}

export interface UpdateTranscriptionRequest {
  utterances?: TranscriptionUtterance[];
  segments?: TranscriptionSegment[];
  customSrt?: string | null;
  speakerNames?: Record<number, string>;
  text?: string;
}

export interface StartUpgradeResponse {
  started: boolean;
  transcriptionId: string | null;
}

export const transcriptionApi = {
  createTranscription: async (data: CreateTranscriptionRequest): Promise<TranscriptionJob> => {
    const response = await api.post<TranscriptionJob>("/transcription", data);
    return response.data;
  },

  getStatus: async (id: string): Promise<TranscriptionJob> => {
    const response = await api.get<TranscriptionJob>(`/transcription/${id}/status`);
    return response.data;
  },

  getResult: async (id: string): Promise<TranscriptionResult> => {
    const response = await api.get<TranscriptionResult>(`/transcription/${id}`);
    return response.data;
  },

  /**
   * Presigned URL на аудио транскрипции для плеера.
   * 404 — аудио недоступно (превью или истёк срок хранения 30 дней).
   */
  getAudioUrl: async (id: string): Promise<{ url: string }> => {
    const response = await api.get<{ url: string }>(`/transcription/${id}/audio`);
    return response.data;
  },

  startUpgrade: async (id: string, model?: "nova-3" | "assemblyai"): Promise<StartUpgradeResponse> => {
    const response = await api.post<StartUpgradeResponse>(`/transcription/${id}/upgrade-start`, { model });
    return response.data;
  },

  createUpgradeIntent: async (id: string): Promise<{ intentId: string }> => {
    const response = await api.post<{ intentId: string }>(`/transcription/${id}/upgrade-intent`);
    return response.data;
  },

  getUserHistory: async (query?: PaginatedHistoryQuery): Promise<PaginatedTranscriptionListResult> => {
    const params = new URLSearchParams();
    if (query?.page) {
      params.append("page", query.page.toString());
    }
    if (query?.limit) {
      params.append("limit", query.limit.toString());
    }
    if (query?.search) {
      params.append("search", query.search);
    }

    const url = `/transcription/history/my${params.toString() ? `?${params.toString()}` : ""}`;
    const response = await api.get<PaginatedTranscriptionListResult>(url);
    return response.data;
  },

  updateTranscription: async (id: string, data: UpdateTranscriptionRequest): Promise<TranscriptionResult> => {
    const response = await api.patch<TranscriptionResult>(`/transcription/${id}`, data);
    return response.data;
  },

  deleteTranscription: async (id: string): Promise<void> => {
    await api.delete(`/transcription/${id}`);
  }
};
